/**
 * Topology-mutating edit operations: delete/dissolve, subdivide,
 * bridge/fill, extrude, inset/bevel/edge-loop-insert command entries,
 * merge, rip, flip/recalculate normals, separate, grid fill, connect
 * vertex path, cleanUp, gridFill.
 *
 * Every op that mutates polygon topology flows through `runEditOp`
 * in `core.ts` — that's the only place undo snapshots, per-vertex
 * attribute reconciliation, and selection clearing are handled.
 * Command-buffer-entry ops (inset / bevel / enterEdgeLoopInsert /
 * extrudeSelection) push their own snapshots because they launch a
 * follow-up command, not a plain mesh mutation.
 */

import {
  buildCurveEditVertices,
  subdivideCurveSelection,
} from "../../modeling/curveOps.ts";
import {
  clearEditSelection,
  deriveVerticesFromEdges,
  deriveVerticesFromFaces,
  facesForOp,
  freezeEditSelection,
  hasAnySelection,
  hasEdgeSelection,
  hasVertexSelection,
  isFaceMode,
  resolveEditModeSelection,
} from "../../modeling/editSelection.ts";
import {
  type MergeStrategy,
  bridgeVertices as meshBridgeVertices,
  cleanUpMesh as meshCleanUpMesh,
  connectVertexPath as meshConnectVertexPath,
  deleteEdges as meshDeleteEdges,
  deleteFaces as meshDeleteFaces,
  deleteOnlyEdgesAndFaces as meshDeleteOnlyEdgesAndFaces,
  deleteOnlyFaces as meshDeleteOnlyFaces,
  deleteVertices as meshDeleteVertices,
  dissolveEdges as meshDissolveEdges,
  dissolveFaces as meshDissolveFaces,
  dissolveVertices as meshDissolveVertices,
  fillFace as meshFillFace,
  flipNormals as meshFlipNormals,
  insertEdgeLoop as meshInsertEdgeLoop,
  mergeVertices as meshMergeVertices,
  recalculateOutside as meshRecalculateOutside,
  ripVertices as meshRipVertices,
  smoothSelection as meshSmoothSelection,
} from "../../modeling/meshOps.ts";
import {
  getDefaultParams,
  usesTopologyPreview,
} from "../../modeling/operatorParams.ts";
import { separateSelection as meshSeparateSelection } from "../../modeling/ops/separate.ts";
import {
  preflightBevelSelection,
  preflightBridgeEdgeLoops,
  preflightBridgeVerticesSelection,
  preflightConnectVertexPath,
  preflightEdgeLoopInsertSeed,
  preflightExtrudeManifold,
  preflightExtrudeSelection,
  preflightFillSelection,
  preflightGridFillSelection,
  preflightInsetSelection,
  resolveSelectedEdgeSeed,
} from "../../modeling/topologyOpPreflight.ts";
import { getCollectionForObject } from "../collectionGraph.ts";
import { regenerateCurveGeometryInMap } from "../curveRegen.ts";
import { clearLiveEditPreview } from "../liveEditPreview.ts";
import { addSceneObject, replaceSceneObject } from "../sceneObjectMap.ts";
import { computeEditPivotCache } from "../storeCommandOps/commandEnter.ts";
import { executeTopologyOp } from "../storeCommandOps/topologyExecute.ts";
import type { CommandType, InteractionMode } from "../storeTypes.ts";
import {
  captureEditObjectSnapshot,
  captureSnapshot,
  commandSnapshotObject,
  undoManager,
} from "../storeUndo.ts";
import {
  notifyTopologyOpFailure,
  type TopologyOpFailureId,
} from "../topologyOpNotifications.ts";
import {
  applyResult,
  computeNormalsForObject,
  type Get_,
  patchSceneObjectAfterEdit,
  replaceSceneObjectAfterEdit,
  ripTargetPoint,
  runEditOp,
  type Set_,
  traceEditOp,
} from "./core.ts";

function showTopologyOpFailure(op: TopologyOpFailureId, reason: string): void {
  notifyTopologyOpFailure(op, reason);
}

function requireResolvedSelection(
  op: TopologyOpFailureId,
  em: NonNullable<ReturnType<Get_>["editMode"]>,
  reason: string,
): boolean {
  if (hasVertexSelection(em)) return true;
  if (import.meta.env.DEV && hasAnySelection(em)) {
    throw new Error(
      `${op}: selection state has explicit elements but no resolved vertex selection`,
    );
  }
  showTopologyOpFailure(op, reason);
  return false;
}

function requireFaceBackedSelection(
  op: TopologyOpFailureId,
  obj: { topology: Parameters<typeof facesForOp>[0] },
  em: NonNullable<ReturnType<Get_>["editMode"]>,
  reason: string,
): Set<number> | null {
  if (!requireResolvedSelection(op, em, reason)) return null;
  const selectedFaces = facesForOp(obj.topology, em);
  if (!selectedFaces || selectedFaces.size === 0) {
    showTopologyOpFailure(op, reason);
    return null;
  }
  return selectedFaces;
}

function startTopologyCommand(
  get: Get_,
  set: Set_,
  type: CommandType,
  previewImmediately: boolean = true,
  opts?: {
    edgeLoopInsertEdge?: [number, number] | null;
  },
): void {
  const state = get();
  const em = state.editMode;
  if (!em) return;
  const obj = state.sceneObjects.get(em.objectId);
  if (!obj) return;

  const snap = captureEditObjectSnapshot(state, em.objectId);
  if (!snap) return;
  const frozen = freezeEditSelection(em);
  // Every topology op consumes px/py/pz in applyLiveEditCommand (via
  // mapParamInput on paramDef.inputMapping). The live-apply layer reads
  // cb.editPivot unconditionally, so this direct-construction path must
  // populate the cache on the same terms as enterCommand.
  const pivotCache = computeEditPivotCache(state, em, snap);
  if (!pivotCache) return;
  set({
    commandBuffer: {
      type,
      axis: null,
      numericInput: "",
      mouseOrigin: null,
      normalVector: null,
      boneLocalAxis: null,
      ...frozen,
      edgeSlideRails: null,
      gizmoInitiated: false,
      edgeLoopInsertEdge: opts?.edgeLoopInsertEdge ?? null,
      edgeLoopHoverEdge: null,
      edgeLoopPreview: null,
      boneCommandData: null,
      proportionalWeights: null,
      editPivot: pivotCache.editPivot,
      editSelectionVertices: pivotCache.editSelectionVertices,
      preEditSelectionIdentity: em.selectedVertices,
      params: getDefaultParams(type),
      activeValueMode: null,
      individualOriginsComponents: null,
      individualOriginsProportionalPivots: null,
      dependentRebuildTargets: null,
      topologyPreviewSeed: null,
      topologyPreviewLastAppliedAt: 0,
    },
    commandPreSnapshot: snap,
    lastOperation: null,
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
  });

  if (!previewImmediately || !usesTopologyPreview(type)) return;

  const current = get();
  const currentEm = current.editMode;
  const currentObj = currentEm
    ? current.sceneObjects.get(currentEm.objectId)
    : null;
  const snapObj = commandSnapshotObject(snap, em.objectId);
  const cb = current.commandBuffer;
  if (!currentEm || !currentObj || !snapObj || !cb) return;

  const previewResult = executeTopologyOp(
    type,
    cb.params,
    new Float32Array(snapObj.vertices),
    snapObj,
    currentEm,
    currentObj,
    current,
    set,
    {
      preEditFaces: cb.preEditFaces,
      preEditSelection: cb.preEditSelection,
      preEditEdges: cb.preEditEdges,
      preEditActiveVertex: cb.preEditActiveVertex,
      preEditActiveEdge: cb.preEditActiveEdge,
      preEditActiveFace: cb.preEditActiveFace,
      edgeLoopInsertEdge: cb.edgeLoopInsertEdge,
    },
    "preview",
    cb.topologyPreviewSeed,
  );
  if (
    previewResult.ok &&
    previewResult.previewSeed &&
    previewResult.previewSeed !== cb.topologyPreviewSeed
  ) {
    set({
      commandBuffer: {
        ...get().commandBuffer!,
        topologyPreviewSeed: previewResult.previewSeed,
        topologyPreviewLastAppliedAt: performance.now(),
      },
    });
  }
}

export function createMeshOps(set: Set_, get: Get_) {
  return {
    // ---- Delete / dissolve ----

    deleteSelectedGeometry: () => {
      const em = get().editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "delete_vertices",
          em,
          "Delete requires a selection.",
        )
      )
        return;

      runEditOp(get, set, "deleteVertices", (obj, em) => {
        const result = meshDeleteVertices(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
        );
        if (!result) {
          showTopologyOpFailure(
            "delete_vertices",
            "Delete could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    deleteSelectedEdges: () => {
      const em = get().editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "delete_edges",
          em,
          "Delete Edges requires a selection.",
        )
      )
        return;

      runEditOp(get, set, "deleteEdges", (obj, em) => {
        const result = meshDeleteEdges(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
        );
        if (!result) {
          showTopologyOpFailure(
            "delete_edges",
            "Delete Edges could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    deleteSelectedFaces: () => {
      const em = get().editMode;
      const obj = em ? get().sceneObjects.get(em.objectId) : null;
      if (!em || !obj) return;
      const selectedFaces = requireFaceBackedSelection(
        "delete_faces",
        obj,
        em,
        "Delete Faces requires a face-backed selection.",
      );
      if (!selectedFaces) return;

      runEditOp(get, set, "deleteFaces", (obj, em) => {
        const result = meshDeleteFaces(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
          selectedFaces,
        );
        if (!result) {
          showTopologyOpFailure(
            "delete_faces",
            "Delete Faces could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    deleteOnlyEdgesAndFaces: () => {
      const em = get().editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "delete_only_edges_and_faces",
          em,
          "Delete Edges/Faces requires a selection.",
        )
      )
        return;

      runEditOp(get, set, "deleteOnlyEdgesAndFaces", (obj, em) => {
        const result = meshDeleteOnlyEdgesAndFaces(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
        );
        if (!result) {
          showTopologyOpFailure(
            "delete_only_edges_and_faces",
            "Delete Edges/Faces could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    deleteOnlyFaces: () => {
      const em = get().editMode;
      const obj = em ? get().sceneObjects.get(em.objectId) : null;
      if (!em || !obj) return;
      const selectedFaces = requireFaceBackedSelection(
        "delete_only_faces",
        obj,
        em,
        "Delete Only Faces requires a face-backed selection.",
      );
      if (!selectedFaces) return;

      runEditOp(get, set, "deleteOnlyFaces", (obj, em) => {
        const result = meshDeleteOnlyFaces(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
          selectedFaces,
        );
        if (!result) {
          showTopologyOpFailure(
            "delete_only_faces",
            "Delete Only Faces could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    dissolveSelectedVertices: () => {
      const em = get().editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "dissolve_vertices",
          em,
          "Dissolve Vertices requires a selection.",
        )
      )
        return;

      runEditOp(get, set, "dissolveVertices", (obj, em) => {
        const result = meshDissolveVertices(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
        );
        if (!result) {
          showTopologyOpFailure(
            "dissolve_vertices",
            "Dissolve Vertices could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    dissolveSelectedEdges: () => {
      const em = get().editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "dissolve_edges",
          em,
          "Dissolve Edges requires a selection.",
        )
      )
        return;

      runEditOp(get, set, "dissolveEdges", (obj, em) => {
        const result = meshDissolveEdges(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
        );
        if (!result) {
          showTopologyOpFailure(
            "dissolve_edges",
            "Dissolve Edges could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    dissolveSelectedFaces: () => {
      const em = get().editMode;
      const obj = em ? get().sceneObjects.get(em.objectId) : null;
      if (!em || !obj) return;
      const selectedFaces = requireFaceBackedSelection(
        "dissolve_faces",
        obj,
        em,
        "Dissolve Faces requires a face-backed selection.",
      );
      if (!selectedFaces) return;

      runEditOp(get, set, "dissolveFaces", (obj, em) => {
        const result = meshDissolveFaces(
          obj.vertices,
          obj.indices,
          obj.topology,
          em.selectedVertices,
          selectedFaces,
        );
        if (!result) {
          showTopologyOpFailure(
            "dissolve_faces",
            "Dissolve Faces could not resolve the current selection.",
          );
          return null;
        }
        return result;
      });
    },

    cleanUpMesh: (): {
      removedFaces: number;
      removedWireEdges: number;
      removedVertices: number;
    } | null => {
      const result = runEditOp(get, set, "cleanUpMesh", (obj) =>
        meshCleanUpMesh(obj.vertices, obj.indices, obj.topology),
      );
      return result
        ? {
            removedFaces: result.removedFaces,
            removedWireEdges: result.removedWireEdges,
            removedVertices: result.removedVertices,
          }
        : null;
    },

    // ---- Subdivide / bridge / fill / connect ----

    subdivideSelection: () => {
      const em = get().editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "subdivide",
          em,
          "Subdivide requires a selection.",
        )
      )
        return;

      // Curve path — not topology-op shaped, so it's handled without runEditOp.
      if (em.isCurve) {
        const obj = get().sceneObjects.get(em.objectId);
        if (!obj?.curveData) return;
        traceEditOp("subdivide", em);
        const result = subdivideCurveSelection(
          obj.curveData,
          em.selectedVertices,
        );
        if (!result) {
          showTopologyOpFailure(
            "subdivide",
            "Subdivide requires a valid curve selection.",
          );
          return;
        }
        undoManager.push(captureSnapshot(get()));
        set({
          sceneObjects: regenerateCurveGeometryInMap(
            get().sceneObjects,
            em.objectId,
            result.curveData,
          ),
          editMode: {
            ...em,
            selectedVertices: result.newSelection,
            curveEditVertices: buildCurveEditVertices(result.curveData),
          },
          canUndo: undoManager.canUndo,
          canRedo: undoManager.canRedo,
        });
        return;
      }

      traceEditOp("subdivide", em);
      startTopologyCommand(get, set, "subdivide");
    },

    bridgeSelectedVertices: () => {
      runEditOp(get, set, "bridgeVertices", (obj, em) => {
        const preflight = preflightBridgeVerticesSelection(obj.topology, em);
        if (!preflight.ok) {
          showTopologyOpFailure("bridge_vertices", preflight.reason);
          return null;
        }
        const [v0, v1] = [...preflight.target.selectedVertices];
        return meshBridgeVertices(obj.vertices, obj.topology, v0!, v1!);
      });
    },

    fillSelection: () => {
      const em = get().editMode;
      if (!em) return;
      const obj = get().sceneObjects.get(em.objectId);
      if (!obj?.topology) return;

      const preflight = preflightFillSelection(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("fill", preflight.reason);
        return;
      }
      if (preflight.target.mode === "bridgeVertices") {
        runEditOp(get, set, "bridgeVertices", (obj, em) => {
          const bridgePreflight = preflightBridgeVerticesSelection(
            obj.topology,
            em,
          );
          if (!bridgePreflight.ok) {
            showTopologyOpFailure("fill", bridgePreflight.reason);
            return null;
          }
          const [v0, v1] = [...bridgePreflight.target.selectedVertices];
          const result = meshBridgeVertices(
            obj.vertices,
            obj.topology,
            v0!,
            v1!,
          );
          if (!result) {
            showTopologyOpFailure(
              "fill",
              "Fill with 2 vertices requires two vertices that are not already connected.",
            );
            return null;
          }
          return result;
        });
      } else {
        runEditOp(get, set, "fillFace", (obj, em) => {
          const resolved = preflightFillSelection(obj.topology, em);
          if (!resolved.ok) {
            showTopologyOpFailure("fill", resolved.reason);
            return null;
          }
          if (resolved.target.mode !== "fillFace") return null;
          const result = meshFillFace(
            obj.vertices,
            obj.indices,
            obj.topology,
            resolved.target.selectedVertices,
          );
          if (!result) {
            showTopologyOpFailure(
              "fill",
              "Fill requires one closed boundary loop.",
            );
            return null;
          }
          return result;
        });
      }
    },

    bridgeEdgeLoops: () => {
      const em = get().editMode;
      const obj = em ? get().sceneObjects.get(em.objectId) : null;
      if (!em || !obj) return;
      traceEditOp("bridgeEdgeLoops", em);
      const preflight = preflightBridgeEdgeLoops(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("bridge_edge_loops", preflight.reason);
        return;
      }
      if (import.meta.env.DEV) {
        const selectedEdges = resolveEditModeSelection(
          obj.topology,
          em,
        ).selectedEdges;
        for (const ei of preflight.target.selectedEdges) {
          if (!selectedEdges.has(ei)) {
            throw new Error(
              `bridgeEdgeLoops: explicit selected edge ${ei} missing from resolved selection`,
            );
          }
        }
      }
      startTopologyCommand(get, set, "bridgeEdgeLoops");
    },

    gridFillSelection: () => {
      const em = get().editMode;
      const obj = em ? get().sceneObjects.get(em.objectId) : null;
      if (!em || !obj) return;
      traceEditOp("gridFill", em);
      const preflight = preflightGridFillSelection(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("grid_fill", preflight.reason);
        return;
      }
      startTopologyCommand(get, set, "gridFill");
    },

    connectVertexPath: () => {
      runEditOp(
        get,
        set,
        "connectVertexPath",
        (obj, em) => {
          const preflight = preflightConnectVertexPath(em);
          if (!preflight.ok) {
            showTopologyOpFailure("connect_vertex_path", preflight.reason);
            return null;
          }
          const result = meshConnectVertexPath(
            obj.vertices,
            obj.indices,
            obj.topology,
            preflight.target.selectedVertices,
            preflight.target.activeVertex,
          );
          if (!result) {
            showTopologyOpFailure(
              "connect_vertex_path",
              "Connect Vertex Path requires selected vertices that share a cuttable face path.",
            );
            return null;
          }
          return result;
        },
        {
          selectionAfter: (_original, cleared, result) => ({
            ...cleared,
            selectedVertices: result.newSelection,
          }),
        },
      );
    },

    // ---- Separate / rip / normals ----

    // Separate is the single edit-mode topology op that does NOT flow through
    // `runEditOp`: it produces a NEW SceneObject (the extracted piece), exits
    // edit mode, and re-selects the new object in object mode. `runEditOp`'s
    // ceremony (patch the editing object, install a post-op editMode) can't
    // express that shape — the op is creating a sibling, not mutating the
    // current edit target. New topology ops should still use `runEditOp` unless
    // they also leave edit mode; this is the one exception, not a template.
    separateSelection: () => {
      const state = get();
      const em = state.editMode;
      if (!em) return;
      if (
        !requireResolvedSelection(
          "separate",
          em,
          "Separate requires a selection.",
        )
      )
        return;
      traceEditOp("separate", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj?.topology) return;

      const result = meshSeparateSelection(
        obj,
        em.selectedVertices,
        facesForOp(obj.topology, em),
      );
      if (!result) {
        showTopologyOpFailure(
          "separate",
          "Separate requires selected faces or detachable loose/wire geometry.",
        );
        return;
      }

      // Gate the new SceneObject through the memory governor BEFORE pushing
      // the undo snapshot, so a refusal leaves no orphan entry on the stack.
      // `addSceneObject` with `user-add` fires the user-facing toast on
      // refusal; we ignore the returned map here and add via `force` further
      // down once the gate has admitted.
      const sepAdmit = addSceneObject(state.sceneObjects, result.separated);
      if (!sepAdmit.admitted) return;

      undoManager.push(captureSnapshot(state));

      // Route the remaining object through applyResult so it gets the same
      // ceremony as every other topology-mutating op: DEV-only topology /
      // mesh-coherence / scene-object validation, normal recomputation, UV
      // regen, per-vertex attribute reconciliation through the provenance,
      // and single-object generator-ref stripping. `separate.ts` already
      // computes most of these internally — applyResult redoes the work
      // but wins consistency with the rest of the edit-op surface, and the
      // op is user-triggered (not per-frame) so the redundancy is fine.
      const remaining = applyResult(
        obj,
        result.remaining,
        "separate-remaining",
      );
      const separated = result.separated;
      let objects = replaceSceneObject(
        state.sceneObjects,
        em.objectId,
        remaining,
      );
      // Add new object — already admitted above, so use `force` to skip the
      // gate (avoids a redundant heap re-read and a duplicate toast).
      objects = addSceneObject(objects, separated, "force").map;

      // Exit edit mode, select the new object.
      // The pre-op snapshot (pushed above) has editSelection set, so undo
      // will return to edit mode with the original mesh and selection intact.
      clearLiveEditPreview();
      set({
        sceneObjects: objects,
        editMode: null,
        mode: "transform" as InteractionMode,
        workspaceMode: "object",
        activeOperator: null,
        selectedObjectTargets: [{ kind: "sceneObject", id: separated.id }],
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
    },

    ripSelection: (mouseX: number, mouseY: number) => {
      const em = get().editMode;
      if (
        !em ||
        !requireResolvedSelection("rip", em, "Rip requires a selection.")
      )
        return;
      // Resolve parent collection once outside runEditOp so the rip math sees
      // the same world frame the overlay is rendered in — needed when the
      // edit target is a collection member.
      const parentCollection = getCollectionForObject(
        get().collections,
        em.objectId,
      );
      const result = runEditOp(
        get,
        set,
        "rip",
        (obj, em) => {
          if (!obj.topology) return null;
          const [mx, my, mz] = ripTargetPoint(
            obj,
            parentCollection,
            em.selectedVertices,
            mouseX,
            mouseY,
          );
          const result = meshRipVertices(
            obj.vertices,
            obj.indices,
            obj.topology,
            em.selectedVertices,
            mx,
            my,
            mz,
          );
          if (!result) {
            showTopologyOpFailure(
              "rip",
              "Rip requires selected vertices that can be split from adjacent geometry.",
            );
            return null;
          }
          return result;
        },
        {
          selectionAfter: (_original, cleared, r) => ({
            ...cleared,
            selectedVertices: r.newSelection,
          }),
        },
      );
      if (result) {
        // Enter translate mode so the user can drag the ripped vertices away.
        get().enterCommand("translate");
      }
    },

    flipNormals: () => {
      // Preserves selection — flipNormals does not change topology.
      runEditOp(
        get,
        set,
        "flipNormals",
        (obj, em) =>
          meshFlipNormals(
            obj.vertices,
            obj.indices,
            obj.topology,
            em.selectedVertices,
          ),
        { preserveSelection: true },
      );
    },

    recalculateOutside: () => {
      // Preserves selection — recalculateOutside does not change topology.
      runEditOp(
        get,
        set,
        "recalculateOutside",
        (obj, em) =>
          meshRecalculateOutside(
            obj.vertices,
            obj.indices,
            obj.topology,
            em.selectedVertices,
          ),
        { preserveSelection: true },
      );
    },

    // ---- Extrude (command-buffer entry) ----

    extrudeSelection: () => {
      const state = get();
      const em = state.editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "extrude",
          em,
          "Extrude requires a selection.",
        )
      )
        return;
      traceEditOp("extrude", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const preflight = preflightExtrudeSelection(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("extrude", preflight.reason);
        return;
      }

      startTopologyCommand(get, set, "extrude");
    },

    extrudeAlongNormalsSelection: () => {
      const state = get();
      const em = state.editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "extrude",
          em,
          "Extrude Along Normals requires a selection.",
        )
      )
        return;
      traceEditOp("extrudeAlongNormals", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const preflight = preflightExtrudeSelection(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("extrude", preflight.reason);
        return;
      }

      startTopologyCommand(get, set, "extrudeAlongNormals");
    },

    extrudeIndividualFacesSelection: () => {
      const state = get();
      const em = state.editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "extrude",
          em,
          "Extrude Individual Faces requires a selection.",
        )
      )
        return;
      traceEditOp("extrudeIndividual", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const preflight = preflightExtrudeSelection(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("extrude", preflight.reason);
        return;
      }
      if (preflight.target.mode !== "faces") {
        showTopologyOpFailure(
          "extrude",
          "Extrude Individual Faces requires a face selection.",
        );
        return;
      }

      startTopologyCommand(get, set, "extrudeIndividual");
    },

    extrudeManifoldSelection: () => {
      const state = get();
      const em = state.editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "extrude",
          em,
          "Manifold Extrude requires a face selection.",
        )
      )
        return;
      traceEditOp("extrudeManifold", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const preflight = preflightExtrudeManifold(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("extrude", preflight.reason);
        return;
      }

      startTopologyCommand(get, set, "extrudeManifold");
    },

    // ---- Inset / bevel / edge loop insert (command-buffer entries) ----

    insetSelection: () => {
      const state = get();
      const em = state.editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "inset",
          em,
          "Inset Faces requires a face-backed selection.",
        )
      )
        return;
      traceEditOp("inset", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const preflight = preflightInsetSelection(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("inset", preflight.reason);
        return;
      }

      startTopologyCommand(get, set, "inset");
    },

    bevelSelection: () => {
      const state = get();
      const em = state.editMode;
      if (
        !em ||
        !requireResolvedSelection(
          "bevel",
          em,
          "Bevel requires selected edges or faces.",
        )
      )
        return;
      traceEditOp("bevel", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const preflight = preflightBevelSelection(obj.topology, em);
      if (!preflight.ok) {
        showTopologyOpFailure("bevel", preflight.reason);
        return;
      }

      startTopologyCommand(get, set, "bevel");
    },

    enterEdgeLoopInsert: (v0?: number, v1?: number) => {
      const state = get();
      const em = state.editMode;
      if (!em) return;
      traceEditOp("edgeLoopInsert", em);
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;

      const autoSeed =
        v0 == null || v1 == null
          ? resolveSelectedEdgeSeed(obj.topology, em)
          : null;
      const seed =
        v0 != null && v1 != null ? ([v0, v1] as [number, number]) : autoSeed;

      if (seed) {
        const seedPreflight = preflightEdgeLoopInsertSeed(
          obj.topology,
          seed[0],
          seed[1],
        );
        if (!seedPreflight.ok) {
          showTopologyOpFailure("edge_loop_insert", seedPreflight.reason);
          return;
        }
        const [seedV0, seedV1] = seedPreflight.target.seedEdge;
        // The selected edge IS the ring direction — use it directly as the ring seed
        const testResult = meshInsertEdgeLoop(
          obj.vertices,
          obj.indices,
          obj.topology,
          seedV0,
          seedV1,
          0.5,
        );
        if (!testResult) {
          showTopologyOpFailure(
            "edge_loop_insert",
            "Edge Loop Insert requires an edge on a cuttable ring.",
          );
          return;
        }
        const loopNewVerts = testResult.newSelection;
        const snap = captureEditObjectSnapshot(state, em.objectId);
        if (!snap) return;
        const frozen = freezeEditSelection(em);
        // Pivot for the slide phase: midpoint of the seed edge in pre-op
        // coordinates. Those indices exist in the snapshot; the new loop
        // verts don't, and mapParamInput only needs a stable reference
        // near the action. Reuses the shared helper with a synthetic em
        // so every topology commandBuffer lands on the same cache code.
        const pivotCache = computeEditPivotCache(
          state,
          {
            objectId: em.objectId,
            selectedVertices: new Set([seedV0, seedV1]),
          },
          snap,
        );
        if (!pivotCache) return;
        set({
          ...replaceSceneObjectAfterEdit(
            state,
            em.objectId,
            applyResult(obj, testResult, "insertEdgeLoop"),
          ),
          editMode: {
            ...clearEditSelection(em),
            selectedVertices: loopNewVerts,
          },
          commandBuffer: {
            type: "edgeLoopInsert" as const,
            axis: null,
            numericInput: "",
            mouseOrigin: null,
            normalVector: null,
            boneLocalAxis: null,
            ...frozen,
            edgeSlideRails: null,
            gizmoInitiated: false,
            edgeLoopInsertEdge: [seedV0, seedV1],
            edgeLoopHoverEdge: null,
            edgeLoopPreview: null,
            boneCommandData: null,
            proportionalWeights: null,
            editPivot: pivotCache.editPivot,
            editSelectionVertices: pivotCache.editSelectionVertices,
            preEditSelectionIdentity: em.selectedVertices,
            params: getDefaultParams("edgeLoopInsert"),
            activeValueMode: null,
            individualOriginsComponents: null,
            individualOriginsProportionalPivots: null,
            dependentRebuildTargets: null,
            topologyPreviewSeed: null,
            topologyPreviewLastAppliedAt: 0,
          },
          commandPreSnapshot: snap,
          lastOperation: null,
          canUndo: undoManager.canUndo,
          canRedo: undoManager.canRedo,
        });
      } else {
        startTopologyCommand(get, set, "edgeLoopInsert", false);
      }
    },

    // ---- Merge ----

    mergeSelection: (strategy: MergeStrategy) => {
      runEditOp(
        get,
        set,
        "mergeVertices",
        (obj, em) =>
          em.selectedVertices.size < 2
            ? null
            : meshMergeVertices(
                obj.vertices,
                obj.indices,
                obj.topology,
                em.selectedVertices,
                strategy,
              ),
        {
          selectionAfter: (_original, cleared, result) => ({
            ...cleared,
            selectedVertices: result.newSelection,
          }),
        },
      );
    },

    mergeByDistanceSelection: () => {
      const em = get().editMode;
      if (!em) return;
      if (em.selectedVertices.size < 2) {
        showTopologyOpFailure(
          "merge_by_distance",
          "Merge by Distance requires at least 2 selected vertices.",
        );
        return;
      }
      traceEditOp("mergeByDistance", em);
      startTopologyCommand(get, set, "mergeByDistance");
    },

    smoothSelection: () => {
      const em = get().editMode;
      if (!em) return;
      if (em.isCurve) return;
      if (!hasAnySelection(em)) {
        showTopologyOpFailure(
          "smooth",
          "Smooth Vertices requires a vertex, edge, or face selection.",
        );
        return;
      }
      traceEditOp("smooth", em);
      // Open the SmoothMenu popover. It captures a baseline on open and
      // calls `smoothSelectionFrom(baseline, ...)` on every slider tick.
      // Mirrors the gravity / relax-bones execute-and-refine pattern —
      // no command buffer, no gizmo arm, no tool palette slot.
      set({ smoothMenuOpen: true });
    },

    smoothSelectionFrom: (
      baseline: Float32Array,
      factor: number,
      iterations: number,
      axisMask: number,
    ) => {
      const state = get();
      const em = state.editMode;
      if (!em || em.isCurve) return;
      const obj = state.sceneObjects.get(em.objectId);
      if (!obj) return;
      const topology = obj.topology;
      if (!topology) return;

      // Resolve the effective vertex set. Face/edge modes contribute
      // their verts; vertex mode passes through. Mirrors Blender's
      // "smooth whatever the current granularity implies" behaviour.
      let verts: Set<number>;
      if (isFaceMode(em)) {
        verts = deriveVerticesFromFaces(topology, em.selectedFaces);
      } else if (em.granularity === "edge" && hasEdgeSelection(em)) {
        verts = deriveVerticesFromEdges(topology, em.selectedEdges);
      } else {
        verts = new Set(em.selectedVertices);
      }
      if (verts.size === 0) return;

      const result = meshSmoothSelection(
        baseline,
        obj.indices,
        topology,
        verts,
        Math.max(1, Math.round(iterations)),
        factor,
        axisMask,
      );
      if (!result) return;

      // Topology is unchanged — patch just the vertices + recomputed
      // normals. Selection stays put because we never touch editMode.
      set({
        ...patchSceneObjectAfterEdit(state, em.objectId, {
          vertices: result.vertices,
          ...computeNormalsForObject(obj, {
            vertices: result.vertices,
            topology,
          }),
        }),
      });
    },
  };
}
